import React from "react";
import { Text, View } from "native-base";
import { Minus, Plus, ShoppingCart } from "lucide-react-native";
import { useUserState } from "../../../../Universal/API/Context-API/hooks/useUserState";
import { useCart } from "../../../../Universal/API/Context-API/CartContext";
import PressableWrapp from "../../../../Universal/Buttons/PressableReact/PressableWrapp";
import {
  getPriceText,
  getUnitCardFacts,
} from "../helpers";
import { UnitCardBodyProps } from "../types";

const UnitCardBody: React.FC<UnitCardBodyProps> = ({
  item,
  isSmallVersion,
  cardWidth,
  styles,
}) => {
  const userState = useUserState();
  const { addItem, decrementItem, getQuantity, incrementItem } = useCart();
  const facts = React.useMemo(() => getUnitCardFacts(item.unit), [item.unit]);
  const quantity = getQuantity(item.unit.id);
  const useCompactFooter = isSmallVersion || (!!cardWidth && cardWidth < 520);
  const useCompactFacts = isSmallVersion || (!!cardWidth && cardWidth < 520);

  const handleAddToCart = React.useCallback((event?: any) => {
    event?.stopPropagation?.();
    addItem(item.unit.id, 1);
  }, [addItem, item.unit.id]);

  const handleIncrement = React.useCallback((event?: any) => {
    event?.stopPropagation?.();
    incrementItem(item.unit.id);
  }, [incrementItem, item.unit.id]);

  const handleDecrement = React.useCallback((event?: any) => {
    event?.stopPropagation?.();
    decrementItem(item.unit.id);
  }, [decrementItem, item.unit.id]);

  return (
    <View style={[styles.body, isSmallVersion ? styles.bodySmall : null]}>
      <Text
        style={[styles.title, isSmallVersion ? styles.titleSmall : null]}
        numberOfLines={3}
      >
        {item.description}
      </Text>

      <View style={[styles.factsRow, useCompactFacts ? styles.factsRowSmall : null]}>
        {facts.map((fact) => (
          <View key={fact} style={[styles.factChip, useCompactFacts ? styles.factChipSmall : null]}>
            <Text style={[styles.factChipText, useCompactFacts ? styles.factChipTextSmall : null]}>{fact}</Text>
          </View>
        ))}
      </View>

      <View style={[styles.footerRow, useCompactFooter ? styles.footerRowSmall : null]}>
        <View style={styles.priceWrap}>
          <Text style={[styles.priceLabel, useCompactFooter ? styles.priceLabelSmall : null]}>Стоимость</Text>
          <Text
            style={[
              styles.priceText,
              isSmallVersion ? styles.priceTextSmall : null,
              !isSmallVersion && useCompactFooter ? styles.priceTextCompactDesktop : null,
            ]}
            numberOfLines={1}
          >
            {getPriceText({
              unit: item,
              isDiscount: userState.isUserLoogedInAndShowDiscountPrices,
              compact: useCompactFooter,
            })}
          </Text>
        </View>

        <View
          style={[
            styles.actionHintsRow,
            useCompactFooter
              ? (quantity > 0 ? styles.actionHintsRowCounterSmall : styles.actionHintsRowSmall)
              : null,
          ]}
        >
          <View
            style={[
              styles.cartSlot,
              useCompactFooter
                ? (quantity > 0 ? styles.cartSlotCounterSmall : styles.cartSlotSmall)
                : null,
            ]}
          >
            {quantity > 0 ? (
              useCompactFooter ? (
                <View style={styles.cartCounterCompactSmall}>
                  <PressableWrapp
                    onPress={handleDecrement}
                    style={[styles.cartCounterGhostButton, styles.cartCounterGhostButtonSmall]}
                  >
                    <Minus size={15} strokeWidth={2.6} color="#101010" />
                  </PressableWrapp>
                  <Text
                    selectable={false}
                    style={[
                      styles.cartCounterValue,
                      isSmallVersion
                        ? styles.cartCounterValueCompactSmall
                        : styles.cartCounterValueCompactDesktop,
                    ]}
                  >
                    {quantity} шт.
                  </Text>
                  <PressableWrapp
                    onPress={handleIncrement}
                    style={[styles.cartCounterPrimaryButton, styles.cartCounterPrimaryButtonSmall]}
                  >
                    <Plus size={15} strokeWidth={2.8} color="#FFFFFF" />
                  </PressableWrapp>
                </View>
              ) : (
                <View style={styles.cartCounterCard}>
                  <View style={styles.cartCounterMeta}>
                    <Text selectable={false} style={styles.cartCounterTitle}>
                      Текущий заказ
                    </Text>
                    <Text selectable={false} style={styles.cartCounterValue}>
                      {quantity} шт.
                    </Text>
                  </View>
                  <View style={styles.cartCounterActions}>
                    <PressableWrapp onPress={handleDecrement} style={styles.cartCounterGhostButton}>
                      <Minus size={16} strokeWidth={2.6} color="#101010" />
                    </PressableWrapp>
                    <PressableWrapp onPress={handleIncrement} style={styles.cartCounterPrimaryButton}>
                      <Plus size={16} strokeWidth={2.8} color="#FFFFFF" />
                    </PressableWrapp>
                  </View>
                </View>
              )
            ) : (
              <PressableWrapp
                onPress={handleAddToCart}
                style={[
                  styles.cartActionButton,
                  useCompactFooter ? styles.cartActionButtonSmall : null,
                ]}
              >
                <View
                  style={[
                    styles.cartActionButtonInner,
                    useCompactFooter ? styles.cartActionButtonInnerSmall : null,
                  ]}
                >
                  <ShoppingCart
                    size={isSmallVersion ? 16 : 16}
                    strokeWidth={2.4}
                    color="#FFFFFF"
                    style={styles.cartActionButtonIcon}
                  />
                  <Text
                    selectable={false}
                    style={[
                      styles.cartActionButtonText,
                      isSmallVersion ? styles.cartActionButtonTextSmall : null,
                    ]}
                  >
                    В корзину
                  </Text>
                </View>
              </PressableWrapp>
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

export default React.memo(UnitCardBody);
